/**
 * Turns the two's complement widget's text fields into an analysis or a named error.
 */

import { parseIntegerInput } from "@/lib/parse-number";
import {
  type Analysis,
  decodePattern,
  encodeDecimal,
  parseDecimal,
  parsePattern,
  validWidth,
} from "./twos";

export type TwosFormat = "decimal" | "binary" | "hex";

export type TwosErrorKey =
  | "errWidth"
  | "errEmpty"
  | "errInvalidDecimal"
  | "errInvalidBinary"
  | "errInvalidHex"
  | "errTooWideBinary"
  | "errTooWideHex";

export type TwosEvaluation =
  | { kind: "error"; key: TwosErrorKey }
  | { kind: "ok"; format: TwosFormat; analysis: Analysis };

const ERROR_FOR: Record<
  "binary" | "hex",
  { invalid: TwosErrorKey; tooWide: TwosErrorKey }
> = {
  binary: { invalid: "errInvalidBinary", tooWide: "errTooWideBinary" },
  hex: { invalid: "errInvalidHex", tooWide: "errTooWideHex" },
};

export function evaluateTwos(
  format: TwosFormat,
  valueText: string,
  widthText: string
): TwosEvaluation {
  const width = parseIntegerInput(widthText);
  if (!validWidth(width)) {
    return { kind: "error", key: "errWidth" };
  }
  if (format === "decimal") {
    const parsed = parseDecimal(valueText);
    if (!parsed.ok) {
      return {
        kind: "error",
        key: parsed.error === "empty" ? "errEmpty" : "errInvalidDecimal",
      };
    }
    return { kind: "ok", format, analysis: encodeDecimal(parsed.value, width) };
  }
  const parsed = parsePattern(valueText, format, width);
  if (!parsed.ok) {
    if (parsed.error === "empty") {
      return { kind: "error", key: "errEmpty" };
    }
    return {
      kind: "error",
      key:
        parsed.error === "tooWide"
          ? ERROR_FOR[format].tooWide
          : ERROR_FOR[format].invalid,
    };
  }
  return { kind: "ok", format, analysis: decodePattern(parsed.pattern, width) };
}
